import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { ArrowForward as ArrowIcon, RecordVoiceOver as AbhaIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';
import { PageShell } from '@/layout/PageShell';
import { StatusPill } from '@/components/StatusPill';
import { me } from '@/api/tenant';

/**
 * Standalone ABHA Creation page under Integration. The full sequential
 * wizard already lives at {@code /tenant/abha} (8-step Aadhaar OTP →
 * suggestions → create address → profile → patient save). This page is
 * the integration-side entry that explains the certification flow and
 * deep-links straight into the wizard with the Aadhaar method preselected.
 */
export function IntegrationAbhaPage() {
  const theme = useTheme();
  const { data: tenantSelf } = useSWR('me-tenant', () => me.tenant());

  return (
    <PageShell
      kicker="TENANT · INTEGRATION · ABHA CREATION"
      title="ABHA Creation"
      subtitle="Sequential 8-step Aadhaar OTP wizard — request OTP → verify → suggestions → create address → profile → patient save. Walk it once for sandbox certification, then port the same calls into your HIMS."
      width="wide"
      actions={tenantSelf && (
        <StatusPill tone={tenantSelf.environment === 'PRODUCTION' ? 'success' : 'info'}>
          {tenantSelf.environment}
        </StatusPill>
      )}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Box sx={{ p: 3, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <AbhaIcon sx={{ fontSize: 28, color: theme.palette.brand.dark }} />
            <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600 }}>What you'll demo</Typography>
          </Stack>
          <Stack spacing={1.25} sx={{ mb: 3 }}>
            {[
              ['1', 'Request Aadhaar OTP', '12-digit Aadhaar; sends OTP to the linked mobile.'],
              ['2', 'Verify Aadhaar OTP', 'Carries the txnId forward + optional communication mobile.'],
              ['3', 'Verify Mobile OTP', 'Only when mobile differs from Aadhaar-linked. Often skipped.'],
              ['4', 'ABHA Address Suggestions', 'ABDM returns 4–6 suggestions for the patient to pick.'],
              ['5', 'Create ABHA Address', 'Finalises the address; returns X-Token for read calls.'],
              ['6', 'Get ABHA Profile', 'Full profile via X-Token — name, gender, DOB, address.'],
              ['7', 'Get ABHA Card (optional)', 'Printable card image (PNG, base64).'],
              ['8', 'Save Patient Profile', 'Persists patient under your tenant; returns UHID for M2 push.'],
            ].map(([n, label, desc]) => (
              <Stack key={n} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: theme.palette.brand.lighter, color: theme.palette.brand.dark, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>{n}</Typography>
                </Box>
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={500}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{desc}</Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button component={RouterLink} to="/tenant/abha" variant="contained" endIcon={<ArrowIcon />} sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}>
              Open ABHA Wizard
            </Button>
            <Button component={RouterLink} to="/tenant/api-explorer" variant="outlined">
              Endpoint-level testing in API Explorer
            </Button>
          </Stack>
        </Box>

        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            The wizard at <code>/tenant/abha</code> drives the full M1 Aadhaar flow with auto-captured txnId / X-Token between steps so each call's output flows into the next form without copy-paste.
          </Alert>
          <Alert severity="success" variant="outlined">
            Already integrated in your HIMS? The same calls are exposed individually in the <RouterLink to="/tenant/api-explorer" style={{ color: 'inherit' }}>API Explorer</RouterLink> with cURL / JS / Python / Java / Postman snippets after each successful run.
          </Alert>
        </Stack>
      </Box>
    </PageShell>
  );
}
